import React, { useState, useEffect } from "react"; // Added useEffect
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { IoCodeSharp } from "react-icons/io5";
import { BiCollapseHorizontal, BiExpandHorizontal } from "react-icons/bi";
import {
  getSortedDataGLOBAL,
  renderTableCell,
  renderTableTotal,
} from "./tableHelper";

const styles = {
  add: {
    table:
      "min-w-full table-auto border-collapse border border-gray-200 bg-white rounded font-roboto",
    theadTr: "",
    th: "border py-2 px-4 text-center text-sm font-light bg-[#283593]",
    thSticky: "",
    tbody: "font-medium",
    trMain: "border-b hover:bg-gray-50",
    td: "border px-3 py-1.5 text-sm",
    tdSticky: "",
    tfoot: "",
    tfootTr: " border-t bg-gray-100 font-bold text-gray-900",
    tfootTd: "border px-3 py-1.5 text-sm",
  },
  performance: {
    table: "min-w-full bg-white shadow-md font-roboto rounded-lg ",
    theadTr: "text-sm leading-normal",
    th: "py-3 px-6 whitespace-nowrap text-left font-bold border-r-2 border-gray-300 bg-[#283593]",
    thSticky: "sticky top-0 z-20",
    thStickyLeft:
      "sticky top-0 left-0 z-30 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)] bg-[#283593]",
    thStickyLeftSubCol: "sticky top-12 left-0 z-30",
    tbody: "text-gray-600 text-sm font-medium",
    trMain:
      "border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150",
    td: "py-3 px-6 text-left border-r-2 border-gray-200 ",
    tdStickyLeft:
      "sticky left-0 bg-white z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-gray-200 ",
    tfoot: "border-t-2 border-gray-300",
    tfootTr: "bg-gray-100 font-bold text-gray-900 w-full",
    tfootTd: "py-3 px-6 text-left border-r border-gray-200",
    tfootTdStickyLeft:
      "sticky left-0 bg-gray-100 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-gray-200",
  },
  reporting: {
    table: "min-w-full bg-white shadow-md font-roboto rounded-lg ",
    theadTr: "text-sm leading-normal",
    th: "py-3 px-6 whitespace-nowrap text-left font-bold border-r-2 border-gray-300 bg-[#283593]",
    thSticky: "sticky top-0 z-20",
    thStickyLeft:
      "sticky top-0 left-0 z-30 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)] bg-[#283593]",
    thStickyLeftSubCol: "sticky top-12 left-0 z-30",
    tbody: "text-gray-600 text-sm font-medium",
    trMain:
      "border-b border-gray-200 hover:bg-gray-50 transition-colors duration-150",
    td: "py-3 px-6 text-left border-r-2 border-gray-200 ",
    tdStickyLeft:
      "sticky left-0 bg-white z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-gray-200 ",
    tfoot: "border-t-2 border-gray-300",
    tfootTr: "bg-gray-100 font-bold text-gray-900 w-full",
    tfootTd: "py-3 px-6 text-left border-r border-gray-200",
    tfootTdStickyLeft:
      "sticky left-0 bg-gray-100 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-gray-200",
  },
};

export const RenderTable = ({
  columns,
  data,
  variant = "add",
  extraColumns = [],
  extraData = [],
  renderCellContent = null,
  showCheckBoxes = false,
  selectedRows = [],
  onSelectRow = () => {},
  onSelectAll = () => {},
  enableExpandCollapse = false,
  currentPage = null,
  rowsPerPage = null,
  hideTotal = false,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentExpandedColumn, setCurrentExpandedColumn] = useState(null);

  useEffect(() => {
    sessionStorage.setItem("pdf_sort_config", JSON.stringify(sortConfig));
  }, [sortConfig]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = getSortedDataGLOBAL(sortConfig, data);

  const paginatedData =
    currentPage && rowsPerPage
      ? sortedData.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage,
        )
      : sortedData;

  const baseColumns = columns.filter((c) => c.visible && !c.temp);

  const flatVisibleColumns = baseColumns.flatMap((col) => {
    if (col.key === currentExpandedColumn) {
      return [
        {
          ...col,
          key: col.key,
          title: "Current Range",
          compare_col_type: "_current",
          temp: true,
        },
        {
          ...col,
          key: col.key,
          title: "Previous Range",
          compare_col_type: "_previous",
          temp: true,
        },
        {
          ...col,
          key: col.key,
          title: "Change",
          compare_col_type: "change",
          temp: true,
        },
        {
          ...col,
          key: col.key,
          type: "percentage",
          calculation: {
            numerator: `${col.key}_current`,
            denominator: `${col.key}_previous`,
            multiplier: 100,
          },
          title: "Change (%)",
          compare_col_type: "change_percentage",
          temp: true,
        },
      ];
    }
    return [col];
  });

  const isFirstVisibleColumn = (cols, col) => {
    return cols.length > 0 && cols[0].key === col.key;
  };

  const isAllSelected =
    sortedData.length > 0 && selectedRows.length === sortedData.length;

  const currentStyle = styles[variant];

  return (
    <div
      className={`w-full overflow-x-auto ${
        variant === "performance" ? "max-h-[70vh] overflow-y-auto" : ""
      }`}
    >
      <table className={`${currentStyle.table} w-full`}>
        <thead className="bg-[#283593] text-white">
          <tr className={currentStyle.theadTr}>
            {showCheckBoxes && variant === "performance" && (
              <th
                className="py-3 px-6 text-left top-0 left-0 z-40 bg-[#283593] shadow-lg"
                rowSpan={currentExpandedColumn ? 2 : 1}
              >
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={onSelectAll}
                />
              </th>
            )}

            {baseColumns.map((col) => {
              const isFirst = isFirstVisibleColumn(baseColumns, col);
              const isExpanded = currentExpandedColumn === col.key;
              const isAnyExpanded = currentExpandedColumn !== null;

              let thClasses = `${currentStyle.th} ${
                variant === "performance" ? currentStyle.thSticky : ""
              }`;
              if (variant === "performance" && isFirst) {
                thClasses = `${currentStyle.th} ${currentStyle.thStickyLeft}`;
              }

              return (
                <th
                  key={col.key}
                  colSpan={isExpanded ? 4 : 1}
                  rowSpan={isAnyExpanded && !isExpanded ? 2 : 1}
                  className={`${thClasses} ${variant === "add" ? "hover:bg-[#3949ab] transition-colors duration-200" : ""}`}
                >
                  <div
                    className={`flex flex-row justify-between items-center w-full h-full ${
                      variant === "add" ? "cursor-pointer" : ""
                    }`}
                    onClick={() => handleSort(col.key)}
                  >
                    <h2 className="flex flex-row items-center whitespace-nowrap select-none">
                      {col.title}
                      {variant === "performance" && col.percentage_diff && (
                        <IoCodeSharp className="mx-2" />
                      )}
                    </h2>

                    <div className="flex flex-row items-center space-x-2 ml-2">
                      {sortConfig.key === col.key && (
                        <span className="transition-transform duration-300 ease-out">
                          {sortConfig.direction === "asc" ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </span>
                      )}

                      {enableExpandCollapse &&
                        col.type !== "string" &&
                        col.type !== "boolean" &&
                        col.nonExpand !== true && (
                          <div
                            className="cursor-pointer text-white/80 hover:text-white p-1 rounded hover:bg-white/20 transition-colors"
                            title={
                              isExpanded
                                ? "Collapse Detailed View"
                                : "Expand to Current & Previous"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentExpandedColumn(
                                isExpanded ? null : col.key,
                              );
                            }}
                          >
                            {isExpanded ? (
                              <BiCollapseHorizontal size={18} />
                            ) : (
                              <BiExpandHorizontal size={18} />
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>

          {currentExpandedColumn !== null && (
            <tr className={currentStyle.theadTr}>
              {baseColumns.map((col) => {
                if (col.key === currentExpandedColumn) {
                  return (
                    <React.Fragment key={`${col.key}-subcols`}>
                      <th
                        className={`${currentStyle.th} ${currentStyle.thStickyLeftSubCol} shadow-md text-xs font-light bg-[#3949ab] text-white align-middle`}
                      >
                        Current Range
                      </th>
                      <th
                        className={`${currentStyle.th} ${currentStyle.thStickyLeftSubCol} text-xs font-light bg-[#3949ab] text-white align-middle`}
                      >
                        Previous Range
                      </th>
                      <th
                        className={`${currentStyle.th} ${currentStyle.thStickyLeftSubCol} text-xs font-light bg-[#3949ab] text-white align-middle`}
                      >
                        Change
                      </th>
                      <th
                        className={`${currentStyle.th} ${currentStyle.thStickyLeftSubCol} text-xs font-light bg-[#3949ab] text-white align-middle`}
                      >
                        Change (%)
                      </th>
                    </React.Fragment>
                  );
                }
                return null;
              })}
            </tr>
          )}
        </thead>

        <tbody className={currentStyle.tbody}>
          {paginatedData.map((item, i) => (
            <React.Fragment key={i}>
              <tr className={currentStyle.trMain}>
                {showCheckBoxes && variant === "performance" && (
                  <td className="py-3 px-6 text-left left-0 z-20 bg-white shadow-md">
                    <input
                      type="checkbox"
                      checked={selectedRows.some(
                        (row) =>
                          row === item ||
                          (row &&
                            item &&
                            row.id !== undefined &&
                            row.id === item.id) ||
                          row === i ||
                          (row && row.id !== undefined && row.id === i),
                      )}
                      onChange={() => onSelectRow(item, i)}
                    />
                  </td>
                )}

                {flatVisibleColumns.map((col, colIdx) => {
                  const isFirst = isFirstVisibleColumn(flatVisibleColumns, col);
                  const tdClasses = `${currentStyle.td} ${
                    variant === "performance" && isFirst
                      ? currentStyle.tdStickyLeft
                      : ""
                  } ${col.temp ? "bg-indigo-50/30" : ""}`;

                  const extraCol = extraColumns.find((c) => c.key === col.key);

                  if (extraCol) {
                    const matched = extraData.find(
                      (d) =>
                        d.column_name.toLowerCase() === col.key.toLowerCase() &&
                        (d.group_id === item.group_id ||
                          d.group_name.toLowerCase() ===
                            item.group_name.toLowerCase()),
                    );
                    return (
                      <td
                        key={`${col.key}-${col.compare_col_type || "base"}-${colIdx}`}
                        className={tdClasses}
                      >
                        {matched ? matched.description : ""}
                      </td>
                    );
                  }

                  return (
                    <td
                      key={`${col.key}-${col.compare_col_type || "base"}-${colIdx}`}
                      className={`${tdClasses} ${["string", "boolean"].indexOf(col.type) === -1 ? "text-right" : ""} ${
                        variant === "reporting" && item.is_difference
                          ? "border-t bg-[#eff0f0]"
                          : ""
                      }`}
                    >
                      {renderCellContent
                        ? renderCellContent(col, item)
                        : renderTableCell(col, item, enableExpandCollapse)}
                    </td>
                  );
                })}
              </tr>

              {item.segments &&
                item.segments.map((segment, subIndex) => (
                  <tr
                    key={`${i}-${subIndex}`}
                    className="border-b border-gray-200 bg-gray-50 hover:bg-gray-100"
                  >
                    {showCheckBoxes && variant === "performance" && (
                      <td className="bg-gray-50 sticky left-0 z-20 border-r-2 border-gray-200" />
                    )}

                    {flatVisibleColumns.map((col, colIdx) => {
                      const isFirst = isFirstVisibleColumn(
                        flatVisibleColumns,
                        col,
                      );
                      const subTdClasses = `${currentStyle.td} ${
                        variant === "performance" && isFirst
                          ? "sticky left-0 bg-gray-50 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                          : ""
                      }`;

                      return (
                        <td
                          key={`${col.key}-${col.compare_col_type || "base"}-${subIndex}-${colIdx}`}
                          className={`${subTdClasses} ${["string", "boolean"].indexOf(col.type) === -1 ? "text-right" : ""}`}
                        >
                          {renderTableCell(col, segment, enableExpandCollapse)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </React.Fragment>
          ))}

          {!hideTotal && sortedData.length > 0 && (
            <tr className={currentStyle.tfootTr}>
              {showCheckBoxes && variant === "performance" && (
                <td className="py-3 px-6 text-left sticky left-0 z-20 bg-gray-100 shadow-md">
                  Total
                </td>
              )}
              {flatVisibleColumns.map((col, idx) => {
                const isFirst = isFirstVisibleColumn(flatVisibleColumns, col);
                const tfootTdClasses = `${currentStyle.tfootTd} ${
                  variant === "performance" && isFirst
                    ? currentStyle.tfootTdStickyLeft
                    : ""
                } ${col.temp ? "bg-indigo-50/30" : ""}`;

                return (
                  <td
                    key={`${col.key}-${col.compare_col_type || "base"}-${idx}`}
                    className={`${tfootTdClasses} ${["string", "boolean"].indexOf(col.type) === -1 ? "text-right" : ""}`}
                  >
                    {idx === 0 && !showCheckBoxes
                      ? "Total"
                      : renderTableTotal(col, sortedData, enableExpandCollapse)}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
